import MarkdownIt from "markdown-it";

import { cn } from "@/lib/utils";

/**
 * Shared markdown renderer for cinch_ops. Uses markdown-it (already in
 * node_modules) configured for the small subset of features we actually
 * write in notes + meeting agendas + AI commentary:
 *
 * - Headings (#, ##, ###)
 * - Bold + italic + inline code
 * - Bullet + numbered lists, nested
 * - Task-list checkboxes (`- [ ]`, `- [x]`)
 * - Block quotes
 * - Links (rendered as new-tab, rel="noreferrer")
 * - Horizontal rules
 *
 * HTML inside the source is **disabled** for safety — the renderer
 * escapes raw HTML. Output is wrapped in a ``prose`` class so the
 * Tailwind typography utilities style it consistently.
 */

const md = new MarkdownIt({
  html: false, // never trust user input as raw HTML
  breaks: true, // single newline → <br> so notes feel like a textarea
  linkify: true, // auto-link bare URLs
  typographer: false,
});

// Transform `- [ ]` and `- [x]` list items into interactive-looking
// checkbox markers. markdown-it ships a task-lists plugin separately, but
// this is small enough to handle inline.
const TASK_OPEN_RE = /^\[ \]\s+/;
const TASK_DONE_RE = /^\[x\]\s+/i;

const originalRender = md.renderer.rules.list_item_open;
md.renderer.rules.list_item_open = function (tokens, idx, options, env, self) {
  const token = tokens[idx];
  const inline = tokens[idx + 2]; // text content of the list item
  if (
    token &&
    inline &&
    inline.type === "inline" &&
    typeof inline.content === "string"
  ) {
    if (TASK_DONE_RE.test(inline.content)) {
      inline.content = inline.content.replace(TASK_DONE_RE, "");
      token.attrJoin("class", "cinch-md-task cinch-md-task-done");
    } else if (TASK_OPEN_RE.test(inline.content)) {
      inline.content = inline.content.replace(TASK_OPEN_RE, "");
      token.attrJoin("class", "cinch-md-task cinch-md-task-open");
    }
  }
  return originalRender
    ? originalRender(tokens, idx, options, env, self)
    : self.renderToken(tokens, idx, options);
};

// Open external links in a new tab with safe rel.
const originalLink =
  md.renderer.rules.link_open ||
  function (tokens, idx, opts, _env, self) {
    return self.renderToken(tokens, idx, opts);
  };
md.renderer.rules.link_open = function (tokens, idx, options, env, self) {
  const t = tokens[idx];
  if (t) {
    t.attrJoin("target", "_blank");
    t.attrJoin("rel", "noreferrer noopener");
  }
  return originalLink(tokens, idx, options, env, self);
};

/** Compile markdown source to a sanitized HTML string. */
export function renderMarkdown(src: string): string {
  if (!src || !src.trim()) return "";
  return md.render(src);
}

/**
 * Drop-in component that renders markdown source as styled HTML.
 *
 * Apply your container's ``className`` for spacing/typography; the
 * ``prose`` styling lives on the rendered children.
 *
 * Safe by default — ``html: false`` is set on the markdown-it instance
 * so raw HTML in the input is escaped, not rendered.
 */
export function MarkdownView({
  source,
  className,
}: {
  source: string;
  className?: string;
}) {
  const html = renderMarkdown(source);
  if (!html) {
    return (
      <p className={cn("text-sm italic text-muted-foreground", className)}>
        _Empty_ — switch to Edit to add content.
      </p>
    );
  }
  return (
    <div
      className={cn(
        // Manual element styling — keeps us free of @tailwindcss/typography
        // (not installed) while still rendering each tag with the cinch_ops
        // visual rhythm. Arbitrary child-selectors target tags the renderer
        // emits.
        "max-w-none text-sm leading-relaxed text-foreground",
        // Headings
        "[&_h1]:mb-2 [&_h1]:mt-3 [&_h1]:text-lg [&_h1]:font-bold",
        "[&_h2]:mb-2 [&_h2]:mt-3 [&_h2]:text-base [&_h2]:font-semibold",
        "[&_h3]:mb-1.5 [&_h3]:mt-2.5 [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:uppercase [&_h3]:tracking-wider [&_h3]:text-muted-foreground",
        // Paragraphs + spacing
        "[&_p]:my-1.5",
        "[&_p+p]:mt-2",
        // Inline emphasis
        "[&_strong]:font-semibold [&_strong]:text-foreground",
        "[&_em]:italic",
        // Code
        "[&_code]:rounded [&_code]:bg-muted [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-xs [&_code]:font-mono",
        "[&_pre]:overflow-x-auto [&_pre]:rounded [&_pre]:bg-muted [&_pre]:p-2 [&_pre]:text-xs",
        // Links
        "[&_a]:text-power [&_a]:underline [&_a]:underline-offset-2 [&_a:hover]:text-power-hover",
        // Lists
        "[&_ul]:my-1.5 [&_ul]:list-disc [&_ul]:pl-5",
        "[&_ol]:my-1.5 [&_ol]:list-decimal [&_ol]:pl-5",
        "[&_li]:my-0.5",
        "[&_li>ul]:my-0 [&_li>ol]:my-0",
        // Block quotes
        "[&_blockquote]:my-2 [&_blockquote]:border-l-2 [&_blockquote]:border-border [&_blockquote]:pl-3 [&_blockquote]:italic [&_blockquote]:text-muted-foreground",
        // Horizontal rules
        "[&_hr]:my-3 [&_hr]:border-t [&_hr]:border-border",
        // Tables (GFM)
        "[&_table]:my-2 [&_table]:w-full [&_table]:border-collapse [&_table]:text-xs",
        "[&_th]:border [&_th]:border-border [&_th]:bg-muted [&_th]:px-2 [&_th]:py-1 [&_th]:text-left [&_th]:font-semibold",
        "[&_td]:border [&_td]:border-border [&_td]:px-2 [&_td]:py-1 [&_td]:align-top",
        // Task list items — wired up in the markdown-it ruleset above.
        "[&_.cinch-md-task-open]:list-none [&_.cinch-md-task-open]:relative [&_.cinch-md-task-open]:pl-5 [&_.cinch-md-task-open]:before:absolute [&_.cinch-md-task-open]:before:left-0 [&_.cinch-md-task-open]:before:content-['☐'] [&_.cinch-md-task-open]:before:text-muted-foreground",
        "[&_.cinch-md-task-done]:list-none [&_.cinch-md-task-done]:relative [&_.cinch-md-task-done]:pl-5 [&_.cinch-md-task-done]:line-through [&_.cinch-md-task-done]:text-muted-foreground [&_.cinch-md-task-done]:before:absolute [&_.cinch-md-task-done]:before:left-0 [&_.cinch-md-task-done]:before:content-['☑'] [&_.cinch-md-task-done]:before:text-success",
        className,
      )}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
